import React from 'react';
import { Box, Typography } from '@mui/material';
import { InfoGrid } from '../../../../../utils/reportUtils';
import { toTitleCase } from '../../../../../utils/commonUtils';
import { EmbryoTransferType } from '../../../../../components/Surgery/embryoTransfer/embryoTransfer.helpers';
import { useTranslation } from 'react-i18next';
import { ClinicHeader } from '../../../../../components/Reports/ClinicHeader';
import PatientDetailsHeader from '../../../../../components/Reports/PatientDetailsHeader';

interface Props {
  data: EmbryoTransferType;
}

const EmbryoTransferReport: React.FC<Props> = ({ data }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');



  const clinicalDetails: string[][] = [
    [tGeneral('clinician'), data.clinician_name || '-', tGeneral('embryologist'), data.embryologist_name || '-'],
    [tSurgery('witnessEmbryologist'), data.witness_embryologist_name || '-', tGeneral('nurse'), data.nurse_name || '-'],
    [tSurgery('transferMethod'), data.transfer_method_label || '-', tSurgery('anesthesia'), data.anesthesia_label || '-'],
    [tGeneral('anesthetist'),data.anesthetist_name || '-',tSurgery('performedUnderAnesthesia'),data.performed_under_anesthesia == null ? '-' : data.performed_under_anesthesia ? tGeneral('Yes') : tGeneral('No'),],
    [tGeneral('startTime'), data.start_time || '-', tGeneral('endTime'), data.end_time || '-'],
    [tSurgery('transferDate'), data.date || '-', tGeneral('cycleStartDate'), data.cycle_start_date || '-'],
  ];

  const procedureDetails: string[][] = [
    [tSurgery('ultrasound'),data.is_ultrasound == null ? '-' : data.is_ultrasound ? tGeneral('Yes') : tGeneral('No'),tSurgery('ultrasoundMethod'),data.ultrasound_method ? toTitleCase(data.ultrasound_method) : '-',],
    [tSurgery('endometriumThickness'), data.endometrium_thickness != null ? data.endometrium_thickness.toString() : '-', tSurgery('endometriumPattern'), data.endometrium_pattern_label || '-', ],
    [tSurgery('transferDepth'), data.transfer_depth != null ? data.transfer_depth.toString() : '-', tSurgery('distanceFromFundus'), data.distance_from_fundus != null ? data.distance_from_fundus.toString() : '-', ],
    [tSurgery('catheter'), data.catheter_label || '-', tSurgery('catheterTube'), data.catheter_tube == null ? '-' : data.catheter_tube ? tGeneral('Yes') : tGeneral('No')],
    [tSurgery('catheterRemarks'), data.catheter_remarks || '-', tSurgery('noOfAttempts'), data.no_of_attempts?.toString() || '-'],
    [tSurgery('embryoRetained'), data.embryo_retained?.toString() || '-', tSurgery('difficultyLevel'), data.difficulty_level_label || '-', ],
    [tSurgery('useOfEmbryoGlue'), data.is_embryo_glue_used == null ? '-' : data.is_embryo_glue_used ? tGeneral('Yes') : tGeneral('No'), tSurgery('useOfTenaculum'), data.is_tenaculum_used == null ? '-' : data.is_tenaculum_used ? tGeneral('Yes') : tGeneral('No'), ],
    [tSurgery('useOfStylet'), data.is_stylet_used == null ? '-' : data.is_stylet_used ? tGeneral('Yes') : tGeneral('No'), '', ''],
  ];

  const pregnancyDetails: string[][] = [
    [ tSurgery('setPregnancyDate'), data.pregnancy_date || '-', tSurgery('cycleClose'), data.is_cycle_closed == null ? '-' : data.is_cycle_closed ? tGeneral('Yes') : tGeneral('No'), ],
    [tSurgery('cycleCloseReason'), data.cycle_close_reason || '-', '', '-'],
  ];

  const currentDateTime = new Date().toLocaleString();

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif', position: 'relative', minHeight: '100%' }}>
      <ClinicHeader />
      <PatientDetailsHeader />

      <Box component="hr" sx={{ my: 1, borderColor: '#fff' }} />

      {/* Clinical Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {tSurgery('clinicalInfo')}
        </Typography>
        <InfoGrid data={clinicalDetails} />
      </Box>

      {/* Procedure Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {tSurgery('procedureDetails')}
        </Typography>
        <InfoGrid data={procedureDetails} />
      </Box>

      {/* Pregnancy Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {tSurgery('pregnancyDetails')}
        </Typography>
        <InfoGrid data={pregnancyDetails} />
      </Box>

      {/* Additional Notes */}
      {data.notes && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {tGeneral('notes')}
          </Typography>
          <Typography variant="body2">{data.notes}</Typography>
        </Box>
      )}

      {/* Complications */}
      {data.complications && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {tGeneral('complications')}
          </Typography>
          <Typography variant="body2">{data.complications}</Typography>
        </Box>
      )}

      {/* Footer Timestamp */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          color: '#666',
          fontStyle: 'italic',
        }}
      >
        {tGeneral('reportGeneratedOn')}: {currentDateTime}
      </Typography>
    </Box>
  );
};

export default EmbryoTransferReport;
